import {
  Badge,
  Button,
  Card,
  CellText,
  DataTable,
  EmptyState,
  FormField,
  Icon,
  Input,
  ListToolbar,
  PageHeader,
  Select,
  Text,
  useToast,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackLink } from '../../../app/BackLink';
import { FormDialog } from '../../../app/FormDialog';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage, formErrors } from '../../../lib/errors';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useSession } from '../../identity/session';
import { usePlants, useSavePlant, type Plant } from '../api';
import { dayMonth, parseDayMonth, period } from '../format';
import styles from './PlantsPage.module.css';

const RATINGS = [0, 1, 2, 3, 4];
const VARIETIES = [
  'spring',
  'rape',
  'acacia',
  'linden',
  'summer',
  'forest',
  'chestnut',
  'heather',
  'blossom',
];

function Rating({ value, label }: { value: number; label: string }) {
  return (
    <span className={styles.rating} role="img" aria-label={`${label}: ${value} / 4`}>
      {RATINGS.slice(1).map((r) => (
        <span key={r} className={r <= value ? styles.dotOn : styles.dot} />
      ))}
    </span>
  );
}

const SCOPES = ['active', 'archived', 'all'] as const;
type Scope = (typeof SCOPES)[number];
const FILTER_KEYS = ['q', 'status'] as const;

/** The forage plant catalog with own plants (TRA-001, TRA-002). */
export function PlantsPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const toast = useToast();
  const plants = usePlants();
  const save = useSavePlant();
  const [scope, setScope] = useUrlState<Scope>('status', 'active', SCOPES);
  const [q, setQ] = useUrlState('q', '');
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const columnSort = useColumnSort();
  const [editing, setEditing] = useState<Plant | 'new' | null>(null);
  const write = can('locations.write');
  const needle = q.trim().toLocaleLowerCase('de');
  const rows = (plants.data ?? [])
    .filter((p) => (scope === 'all' ? true : scope === 'archived' ? p.archived : !p.archived))
    .filter(
      (p) =>
        !needle ||
        [p.name, p.latin_name ?? ''].some((s) => s.toLocaleLowerCase('de').includes(needle)),
    );
  const filtered = scope !== 'active' || needle !== '';

  const setArchived = (p: Plant, archived: boolean) =>
    save.mutate(
      { id: p.id, version: p.version, body: { ...p, archived } },
      {
        onError: (err) => toast.show(errorMessage(err), 'danger'),
        onSuccess: (saved) =>
          // Archiving is reversible, so it happens at once with an undo (§6.10).
          toast.show(
            archived ? t('forage.plants.archivedToast') : t('forage.plants.restored'),
            'success',
            archived
              ? {
                  label: t('common.undo'),
                  onClick: () =>
                    save.mutate({
                      id: saved.id,
                      version: saved.version,
                      body: { ...saved, archived: false },
                    }),
                }
              : undefined,
          ),
      },
    );

  const columns: DataTableColumn<Plant>[] = [
    {
      key: 'name',
      header: t('forage.plants.columns.name'),
      primary: true,
      cell: (p) => (
        <CellText
          main={
            <>
              {p.name}
              {p.archived && (
                <>
                  {' '}
                  <Badge size="sm">{t('forage.plants.archived')}</Badge>
                </>
              )}
            </>
          }
          sub={[p.latin_name, p.key ? null : t('forage.plants.own')].filter(Boolean).join(' · ')}
        />
      ),
      sortValue: (p) => p.name,
    },
    {
      key: 'bloom',
      header: t('forage.plants.columns.bloom'),
      cell: (p) => period(p.bloom_start, p.bloom_end),
      // Month and day ("MM-DD"), so the order follows the calendar.
      sortValue: (p) => p.bloom_start.slice(-5),
    },
    {
      key: 'nectar',
      header: t('forage.plant.nectar'),
      mobile: 'hidden',
      cell: (p) => <Rating value={p.nectar} label={t('forage.plant.nectar')} />,
      sortValue: (p) => p.nectar,
    },
    {
      key: 'pollen',
      header: t('forage.plant.pollen'),
      mobile: 'hidden',
      cell: (p) => <Rating value={p.pollen} label={t('forage.plant.pollen')} />,
      sortValue: (p) => p.pollen,
    },
    {
      key: 'variety',
      header: t('forage.plants.columns.variety'),
      cell: (p) => (p.variety_key ? t(`forage.variety.${p.variety_key}` as never) : '–'),
      sortValue: (p) => (p.variety_key ? t(`forage.variety.${p.variety_key}` as never) : null),
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      <PageHeader
        back={<BackLink to="/standorte" label={t('apiary.locations.title')} />}
        title={t('forage.plants.title')}
        actions={
          write && (
            <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setEditing('new')}>
              {t('forage.plants.new')}
            </Button>
          )
        }
      />
      <ListToolbar
        sort={sortMenu}
        search={{
          value: q,
          onChange: setQ,
          label: t('common.search'),
          clearLabel: t('common.clear'),
        }}
        scope={{
          label: t('forage.plants.scope'),
          value: scope,
          onChange: (v) => setScope(v as Scope),
          options: [
            { value: 'active', label: t('apiary.statusFilter.active') },
            { value: 'archived', label: t('forage.plants.archived') },
            { value: 'all', label: t('apiary.statusFilter.all') },
          ],
        }}
      />
      <Card padding="none">
        {plants.error ? (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(plants.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('forage.plants.title')}
            rows={rows}
            loading={plants.isPending}
            rowKey={(p) => p.id}
            {...columnSort}
            columns={columns}
            {...(write
              ? {
                  rowActions: rowMenu.table<Plant>(
                    (p) => p.name,
                    (p) => [
                      !p.key && rowMenu.edit(() => setEditing(p)),
                      {
                        key: 'archive',
                        label: p.archived ? t('forage.plants.restore') : t('forage.plants.archive'),
                        onSelect: () => setArchived(p, !p.archived),
                      },
                    ],
                  ),
                }
              : {})}
            empty={
              filtered ? (
                <EmptyState
                  title={t('common.noResults')}
                  actions={
                    <Button variant="secondary" onClick={resetFilters}>
                      {t('common.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title={t('forage.plants.emptyTitle')}
                  actions={
                    write && (
                      <Button onClick={() => setEditing('new')}>{t('forage.plants.new')}</Button>
                    )
                  }
                />
              )
            }
          />
        )}
      </Card>
      {editing && (
        <PlantDialog
          plant={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function PlantDialog({ plant, onClose }: { plant?: Plant | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSavePlant();
  const [name, setName] = useState(plant?.name ?? '');
  const [latin, setLatin] = useState(plant?.latin_name ?? '');
  const [start, setStart] = useState(plant ? dayMonth(plant.bloom_start) : '');
  const [end, setEnd] = useState(plant ? dayMonth(plant.bloom_end) : '');
  const [nectar, setNectar] = useState(String(plant?.nectar ?? 2));
  const [pollen, setPollen] = useState(String(plant?.pollen ?? 2));
  const [variety, setVariety] = useState(plant?.variety_key ?? '');
  const [dateError, setDateError] = useState(false);
  const errors = formErrors(save.error, {
    name: ['plant.name', 'plant.exists'],
    bloom: ['bloom.'],
  });
  const ratingOptions = RATINGS.map((r) => ({
    value: String(r),
    label: t(`forage.rating.${r}` as never),
  }));

  const submit = () => {
    const s = parseDayMonth(start);
    const e = parseDayMonth(end);
    if (!s || !e) {
      setDateError(true);
      return;
    }
    setDateError(false);
    save.mutate(
      {
        id: plant?.id,
        version: plant?.version,
        body: {
          name,
          latin_name: latin.trim() || null,
          bloom_start: s,
          bloom_end: e,
          nectar: Number(nectar),
          pollen: Number(pollen),
          variety_key: variety || null,
          archived: plant?.archived ?? false,
        },
      },
      {
        onSuccess: () => {
          toast.show(t('common.saved'));
          onClose();
        },
      },
    );
  };

  return (
    <FormDialog
      open
      onClose={onClose}
      title={plant ? t('forage.plants.editTitle') : t('forage.plants.newTitle')}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={errors.general}
      onSubmit={submit}
    >
      <FormField label={t('forage.plants.columns.name')} required error={errors.field.name}>
        {(p) => (
          <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        )}
      </FormField>
      <FormField label={t('forage.plants.latin')}>
        {(p) => (
          <Input {...p} value={latin} maxLength={120} onChange={(e) => setLatin(e.target.value)} />
        )}
      </FormField>
      <div className={styles.pair}>
        <FormField
          label={t('forage.dialog.start')}
          required
          error={dateError ? t('forage.dialog.invalid') : errors.field.bloom}
        >
          {(p) => (
            <Input
              {...p}
              value={start}
              inputMode="decimal"
              placeholder="15.04."
              onChange={(e) => setStart(e.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('forage.dialog.end')} required>
          {(p) => (
            <Input
              {...p}
              value={end}
              inputMode="decimal"
              placeholder="31.05."
              onChange={(e) => setEnd(e.target.value)}
            />
          )}
        </FormField>
      </div>
      <div className={styles.pair}>
        <FormField label={t('forage.plant.nectar')}>
          {(p) => (
            <Select
              {...p}
              value={nectar}
              onChange={(e) => setNectar(e.target.value)}
              options={ratingOptions}
            />
          )}
        </FormField>
        <FormField label={t('forage.plant.pollen')}>
          {(p) => (
            <Select
              {...p}
              value={pollen}
              onChange={(e) => setPollen(e.target.value)}
              options={ratingOptions}
            />
          )}
        </FormField>
      </div>
      <FormField label={t('forage.plants.columns.variety')}>
        {(p) => (
          <Select
            {...p}
            value={variety}
            onChange={(e) => setVariety(e.target.value)}
            options={[
              { value: '', label: t('forage.plants.noVariety') },
              ...VARIETIES.map((v) => ({ value: v, label: t(`forage.variety.${v}` as never) })),
            ]}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
